import { FunctionComponent, ReactNode } from 'react';

import styled, { css } from 'styled-components';

import { colors } from 'chime-ui/system';

import { ButtonSpinnerIcon } from '../Icons';

export const StyledButton = styled.button`
  display: block;
  min-width: 80px;
  width: 100%;
  height: 3rem;
  font-family: inherit;
  font-size: 1rem;
  font-weight: 700;
  text-shadow: none;
  line-height: 1.5rem;
  border-radius: 1.5rem;
  box-shadow: none;
  transition: all 0.3s ease 0s;
  padding: 0.75rem 1rem;
  color: ${colors.notBlack};
  background-color: ${colors.brightGreen};
  border: none;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: 0 4px 12px rgba(51, 51, 51, 0.24);
  }

  &:active {
    outline: none;
    opacity: 0.6;
  }

  ${props =>
    (props.disabled || props.loading) &&
    css`
      opacity: 0.4;
      pointer-events: none;
    `};
`;

export type ButtonType = React.HTMLProps<HTMLButtonElement> & {
  children?: ReactNode;
  loading?: boolean;
};

export const Button: FunctionComponent<ButtonType> = ({
  loading,
  children,
  ...props
}) => (
  <StyledButton type={'button'} {...props}>
    {loading ? <ButtonSpinnerIcon /> : children}
  </StyledButton>
);

export const AltButton = styled(StyledButton)`
  background-color: ${colors.white};
  box-shadow: 0 4px 12px rgba(51, 51, 51, 0.16);
`;

export const SecondaryButton = styled(StyledButton)`
  color: ${colors.darkGreen};
  background: none;
`;

export const ghostButtonMixin = css`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`;

export const GhostButton = styled.button`
  ${ghostButtonMixin}
`;

export const UnderlinedGhostButton = styled.button`
  ${ghostButtonMixin};
  text-decoration: underline;
  font-weight: 500;
`;

export const UnderlinedBoldGhostButton = styled.button`
  ${ghostButtonMixin};
  text-decoration: underline;
  font-weight: 700;
`;

type IconButtonProps = {
  icon: Node;
};

export const IconButton = styled.button.attrs(({ icon }: IconButtonProps) => ({
  children: icon || null,
  type: 'button',
}))`
  ${ghostButtonMixin};
  &:disabled {
    opacity: 0.5;
    cursor: auto;
  }
  &:focus {
    outline: 2px solid ${colors.darkGreen};
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
`;
